/**
 * @file useUnreadCount.js
 * @brief Tracks unread conversation and moderation counts, websocket listeners, and notification side effects.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { createApiClient } from "../api/axios";
import { useUserProfile } from "../context/UserProfileContext";
import { useMessageNotifications } from "./useMessageNotifications";
import { useModerationNotifications } from "./useModerationNotifications";
import { getUserDisplayName } from "../utils/userDisplayName";

const WS_BASE = process.env.REACT_APP_WS_BASE_URL || "ws://localhost:7000";

/**
 * Compare conversation unread snapshots to avoid redundant state updates.
 * @param {Array<Object>} a First conversation list.
 * @param {Array<Object>} b Second conversation list.
 * @returns {boolean} True when both snapshots describe the same unread state.
 */
function areConversationKeysEqual(a = [], b = []) {
    if (a === b) return true;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i += 1) {
        const left = a[i] || {};
        const right = b[i] || {};

        if (String(left.listing_id ?? "") !== String(right.listing_id ?? "")) return false;
        if (String(left.other_user?.id ?? "") !== String(right.other_user?.id ?? "")) return false;
        if ((left.unread_count || 0) !== (right.unread_count || 0)) return false;
    }

    return true;
}

function isValidId(value) {
    if (value === null || value === undefined) return false;
    const asString = String(value).trim().toLowerCase();
    return asString !== "" && asString !== "null" && asString !== "undefined";
}

/**
 * Maintain unread counts and subscribe to realtime notification channels for the signed-in user.
 * @param {Object} auth Authentication state from the OIDC context.
 * @param {string} pathname Current location path used to suppress duplicate notifications.
 * @returns {{unread: number, moderationUnread: number, requestNotificationPermission: Function}} Unread counters and notification helpers.
 */
export function useUnreadCount(auth, pathname) {
    const accessToken = auth.user?.access_token;
    const isAuthenticated = auth.isAuthenticated;
    const { profile } = useUserProfile();
    const groups = profile?.groups || [];
    const isAdminOrModerator = groups.includes("admins") || groups.includes("moderators");
    const [unread, setUnread] = useState(0);
    const [moderationUnread, setModerationUnread] = useState(0);
    const [conversations, setConversations] = useState([]);
    const socketsRef = useRef(new Map());
    const userSocketRef = useRef(null);
    const refreshRef = useRef(null);
    const previousModerationCountRef = useRef(null);
    const {
        notifyIncomingMessage,
        notifyIncomingTransaction,
        requestNotificationPermission,
        clearConversationNotification,
    } = useMessageNotifications({
        enabled: auth.isAuthenticated,
        pathname,
    });
    const { notifyIncomingModerationListing } = useModerationNotifications({ pathname });

    const refreshUnread = useCallback(async () => {
        try {
            const api = createApiClient({
                isAuthenticated,
                user: accessToken ? { access_token: accessToken } : null,
            });
            const res = await api.get("messages/conversations/");
            const list = res.data || [];
            setConversations((prev) => (areConversationKeysEqual(prev, list) ? prev : list));
            const total = list.reduce((sum, c) => sum + (c.unread_count || 0), 0);
            setUnread((prev) => (prev === total ? prev : total));
        } catch {
            // silently ignore
        }
    }, [accessToken, isAuthenticated]);

    const refreshModerationUnread = useCallback(async ({ notifyOnIncrease = true } = {}) => {
        if (!isAdminOrModerator || !accessToken) {
            setModerationUnread(0);
            previousModerationCountRef.current = null;
            return;
        }

        try {
            const api = createApiClient({
                isAuthenticated,
                user: accessToken ? { access_token: accessToken } : null,
            });
            const res = await api.get("listings/?status=pend_app&page=1");
            const pendingCount = Number(res?.data?.count || 0);
            const previousCount = previousModerationCountRef.current;

            setModerationUnread((prev) => (prev === pendingCount ? prev : pendingCount));
            previousModerationCountRef.current = pendingCount;

            if (notifyOnIncrease && previousCount !== null && pendingCount > previousCount) {
                notifyIncomingModerationListing({
                    event: "queue_update",
                    count: pendingCount - previousCount,
                    listing_id: `queue-${pendingCount}-${Date.now()}`,
                });
            }
        } catch {
            // silently ignore
        }
    }, [accessToken, isAdminOrModerator, isAuthenticated, notifyIncomingModerationListing]);

    useEffect(() => {
        refreshRef.current = refreshUnread;
    }, [refreshUnread]);

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.user?.access_token) return;
        refreshUnread();
        refreshModerationUnread();
        const interval = setInterval(() => {
            refreshUnread();
            refreshModerationUnread();
        }, 30_000);
        return () => clearInterval(interval);
    }, [auth.isAuthenticated, auth.user?.access_token, refreshModerationUnread, refreshUnread]);

    useEffect(() => {
        if (!auth.isAuthenticated) {
            setModerationUnread(0);
            previousModerationCountRef.current = null;
            return;
        }

        if (!isAdminOrModerator) {
            setModerationUnread(0);
            previousModerationCountRef.current = null;
        }
    }, [auth.isAuthenticated, isAdminOrModerator]);

    useEffect(() => {
        if (!isAdminOrModerator) return;

        /**
         * Apply a local moderation queue delta emitted by UI actions before the next server refresh.
         * @param {CustomEvent} event Event carrying the queue delta in `detail.delta`.
         * @returns {void}
         */
        const handleModerationQueueDelta = (event) => {
            const rawDelta = Number(event?.detail?.delta || 0);
            if (!rawDelta) return;

            setModerationUnread((prev) => Math.max(0, prev + rawDelta));

            if (previousModerationCountRef.current !== null) {
                previousModerationCountRef.current = Math.max(0, previousModerationCountRef.current + rawDelta);
            }
        };

        window.addEventListener("moderation:queue-delta", handleModerationQueueDelta);
        return () => {
            window.removeEventListener("moderation:queue-delta", handleModerationQueueDelta);
        };
    }, [isAdminOrModerator]);

    useEffect(() => {
        const token = auth.user?.access_token;
        const sockets = socketsRef.current;

        if (!auth.isAuthenticated || !token) {
            sockets.forEach((ws) => ws.close());
            sockets.clear();
            return;
        }

        const desiredKeys = new Set(
            conversations
                .filter((conv) => isValidId(conv?.listing_id) && isValidId(conv?.other_user?.id))
                .map((conv) => `${conv.listing_id}-${conv.other_user.id}`)
        );

        sockets.forEach((ws, key) => {
            if (!desiredKeys.has(key)) {
                ws.close();
                sockets.delete(key);
            }
        });

        conversations.forEach((conv) => {
            const listingId = conv?.listing_id;
            const otherUserId = conv?.other_user?.id;
            if (!isValidId(listingId) || !isValidId(otherUserId)) return;

            const key = `${listingId}-${otherUserId}`;
            if (sockets.has(key)) return;

            const ws = new WebSocket(`${WS_BASE}/ws/messages/${listingId}/${otherUserId}/?token=${token}`);
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (
                        data.type === "chat.message" &&
                        String(data.message?.sender?.id) === String(otherUserId)
                    ) {
                        notifyIncomingMessage(data.message, {
                            listingId,
                            otherUserId,
                            fallbackName: getUserDisplayName(conv?.other_user?.user).primaryName,
                        });
                    }
                    if (data.type === "chat.read") {
                        clearConversationNotification(listingId, otherUserId);
                    }
                    if (data.type === "chat.message" || data.type === "chat.read") {
                        refreshRef.current?.();
                    }
                } catch {
                    // ignore malformed event payloads
                }
            };

            ws.onerror = () => {
                // silently ignore transient socket errors in unread listener
            };

            ws.onclose = () => {
                sockets.delete(key);
            };

            sockets.set(key, ws);
        });
    }, [
        auth.isAuthenticated,
        auth.user?.access_token,
        clearConversationNotification,
        conversations,
        notifyIncomingMessage,
    ]);

    useEffect(() => {
        const token = auth.user?.access_token;

        if (!auth.isAuthenticated || !token) {
            userSocketRef.current?.close();
            userSocketRef.current = null;
            return;
        }

        const ws = new WebSocket(`${WS_BASE}/ws/notifications/?token=${token}`);

        ws.onopen = () => {
            if (isAdminOrModerator) {
                try {
                    ws.send(JSON.stringify({ type: "subscribe", channel: "moderation" }));
                } catch {
                    // ignore socket send races
                }
            }
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === "user.message" && data.message) {
                    const msg = data.message;
                    const listingId = data.listing_id ?? msg.listing;
                    const otherUserId = data.other_user_id ?? msg.sender?.id;
                    const fallbackName = getUserDisplayName(
                        msg.sender?.user,
                        data.other_username
                    ).primaryName;

                    if (listingId && otherUserId) {
                        notifyIncomingMessage(msg, {
                            listingId,
                            otherUserId,
                            fallbackName,
                        });
                    }

                    refreshRef.current?.();
                    return;
                }

                if (data.type === "user.transaction") {
                    notifyIncomingTransaction(data, {
                        listingId: data.listing_id,
                        otherUserId: data.other_user_id,
                        fallbackName: data.other_username,
                    });
                    refreshRef.current?.();
                    return;
                }

                if (data.type === "user.moderation_listing") {
                    const eventDelta = Math.max(1, Number(data.count || 1));
                    setModerationUnread((prev) => prev + eventDelta);
                    previousModerationCountRef.current = (previousModerationCountRef.current ?? 0) + eventDelta;

                    notifyIncomingModerationListing(data);
                    refreshModerationUnread({ notifyOnIncrease: false });
                }
            } catch {
                // ignore malformed payloads
            }
        };

        userSocketRef.current = ws;

        return () => {
            ws.close();
            if (userSocketRef.current === ws) {
                userSocketRef.current = null;
            }
        };
    }, [
        auth.isAuthenticated,
        auth.user?.access_token,
        isAdminOrModerator,
        notifyIncomingMessage,
        notifyIncomingModerationListing,
        notifyIncomingTransaction,
        refreshModerationUnread,
    ]);

    useEffect(() => {
        const sockets = socketsRef.current;

        return () => {
            sockets.forEach((ws) => ws.close());
            sockets.clear();
        };
    }, []);

    return { unread, moderationUnread, requestNotificationPermission };
}
